import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useUserRole } from "@/hooks/useUserRole";
import { useAuth } from "@/contexts/AuthContext";
import { Wrench } from "lucide-react";
import AdminMaintenanceBanner from "./AdminMaintenanceBanner";

interface Window {
  id: string;
  starts_at: string;
  ends_at: string;
  message: string | null;
  is_active: boolean;
}

const ALLOWED_PATHS = new Set([
  "/login",
  "/auth/callback",
  "/mfa",
  "/forgot-password",
  "/reset-password",
  "/unsubscribe",
]);

const formatWhen = (iso: string) =>
  new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/London",
    timeZoneName: "short",
  }).format(new Date(iso));

const MaintenanceGate = ({ children }: { children: React.ReactNode }) => {
  const [win, setWin] = useState<Window | null>(null);
  const { role } = useUserRole();
  const { user } = useAuth();
  const { pathname } = useLocation();

  useEffect(() => {
    let cancelled = false;
    const fetchWin = async () => {
      const { data } = await (supabase as any).rpc("get_active_or_next_maintenance_window");
      if (cancelled) return;
      const row = (data && data[0]) || null;
      setWin(row);
    };
    void fetchWin();
    // Poll every 60s instead of opening a realtime WebSocket. The maintenance
    // gate doesn't need sub-second freshness, and avoiding the WS subscription
    // prevents console errors (ERR_NAME_NOT_RESOLVED / blocked WS) on
    // restrictive networks and during Lighthouse audits.
    const interval = window.setInterval(fetchWin, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, []);

  // Admin bypass: allow normal use plus a ?bypass=1 escape hatch on any path
  const isAdmin = role === "admin";
  const bypassFlag = new URLSearchParams(window.location.search).get("bypass") === "1";
  const onAllowedPath = ALLOWED_PATHS.has(pathname);

  // Admins (and anyone bypassing) see the app normally, but admins also get a
  // top-of-page banner when a window is scheduled but not yet active.
  if (!win || !win.is_active || isAdmin || bypassFlag || onAllowedPath) {
    const showAdminBanner = isAdmin && win && !win.is_active;
    return (
      <>
        {showAdminBanner && (
          <AdminMaintenanceBanner
            windowId={win.id}
            startsAt={win.starts_at}
            endsAt={win.ends_at}
          />
        )}
        {children}
      </>
    );
  }

  return (
    <div className="min-h-dvh flex items-center justify-center bg-background p-6">
      <div className="max-w-md w-full text-center">
        <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-6">
          <Wrench className="w-8 h-8 text-primary" />
        </div>
        <h1 className="text-2xl font-bold text-foreground mb-3">We'll be back shortly</h1>
        <p className="text-muted-foreground mb-4">
          GetPetVet is undergoing scheduled maintenance. We expect to be back online by{" "}
          <strong className="text-foreground">{formatWhen(win.ends_at)}</strong>.
        </p>
        {win.message && (
          <p className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-4 mb-4">
            {win.message}
          </p>
        )}
        <p className="text-xs text-muted-foreground">
          Thanks for your patience{user ? "" : ""}.
        </p>
      </div>
    </div>
  );
};

export default MaintenanceGate;
